import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCreditCheck } from '../hooks/useCreditCheck';
import { Loader } from './Loader';

interface ProtectedRouteProps {
    children: React.ReactNode;
    allowedRoles?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    allowedRoles
}) => {
    const { user, profile, loading, isSuperAdmin } = useAuth();
    const { checkCanAccessTool } = useCreditCheck();
    const location = useLocation();

    if (loading) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-black text-zinc-500">
                <Loader />
            </div>
        );
    }

    // Not authenticated - redirect to login
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Account inactive - redirect to locked page (unless superadmin)
    const isActive = profile?.is_active || profile?.role === 'superadmin';
    
    if (!isActive && !isSuperAdmin && location.pathname !== '/locked') {
        return <Navigate to="/locked" replace />;
    }

    // Role-based access control
    if (allowedRoles && allowedRoles.length > 0) {
        if (!isSuperAdmin) {
            if (!profile || !allowedRoles.includes(profile.role)) {
                return <Navigate to="/app" replace />;
            }
        }
    }

    // Tool-specific access check disabled - causes store sync issues
    // Basic isActive check above is sufficient for now

    return <>{children}</>;
};
